/** Builds the contract lifecycle rail: the four states plus the check-in / check-out
 *  inspection milestones, each with its display state and the tab it anchors to. */
import type { LifecycleStep, StepState } from "@/components/LifecycleStepper";
import type { Requirement } from "@/hooks/api/useRequirements";
import type { components } from "@/types/api";

type Inspection = components["schemas"]["InspectionListItem"];

// Which requirement gate informs each lifecycle state's "what's needed" line.
const GATE_FOR_STATUS: Record<string, "activate" | "close"> = {
  draft: "activate",
  ending: "close",
};
const GATE_VERB = { activate: "To activate", close: "To close" } as const;

const HINT: Record<string, string> = {
  draft:
    "To activate: generate & sign the lease. The booking confirmation, deposit invoice and check-in usually come first.",
  active:
    "To wind down: set an end date (give notice). Rent invoices, the landlord overview and indexation letters run while active.",
  ending:
    "To close: complete the signed check-out inspection, then issue the deposit return.",
  closed: "Closed — the tenancy is settled.",
};

const RANK: Record<string, number> = { draft: 0, active: 1, ending: 2, closed: 3 };

function done(insp: Inspection | undefined): boolean {
  return !!insp && (insp.status === "completed" || insp.status === "signed");
}

/** A live "what's needed to continue" line drawn from the gate's requirements:
 *  e.g. "To activate — 2 of 4 ready. Waiting on Tenant ID, Energy label (rejected)." */
function requirementHint(
  status: string,
  requirements: Requirement[],
): string | undefined {
  const gate = GATE_FOR_STATUS[status];
  if (!gate) return undefined;
  const items = requirements.filter((r) => r.gate === gate);
  if (items.length === 0) return undefined;

  const ready = items.filter((r) => r.status === "accepted").length;
  if (ready === items.length) {
    return `${GATE_VERB[gate]} — all ${items.length} documents ready.`;
  }

  const pending = items.filter((r) => r.status !== "accepted");
  const names = pending
    .slice(0, 3)
    .map((r) => (r.status === "rejected" ? `${r.subject} (rejected)` : r.subject));
  const more = pending.length - names.length;
  const tail = more > 0 ? `, +${more} more` : "";
  return `${GATE_VERB[gate]} — ${ready} of ${items.length} ready. Waiting on ${names.join(", ")}${tail}.`;
}

export function buildMilestones(
  status: string,
  inspections: Inspection[],
  requirements: Requirement[] = [],
): { steps: LifecycleStep[]; hint?: string } {
  if (status === "cancelled") {
    return {
      steps: [
        { key: "draft", label: "Draft", state: "done", tab: "documents" },
        { key: "cancelled", label: "Cancelled", state: "cancelled" },
      ],
      hint: "This draft was cancelled — no further steps.",
    };
  }

  const rank = RANK[status] ?? 0;
  const checkIn = inspections.find((i) => i.type === "check_in");
  const checkOut = inspections.find((i) => i.type === "check_out");

  // A check-in/out node: done when its inspection is; pending (current) once the
  // tenancy reaches the stage where it's due; otherwise upcoming or passed.
  const inspectionState = (
    insp: Inspection | undefined,
    dueFrom: "active" | "ending",
  ): StepState => {
    if (done(insp)) return "done";
    if (status === "closed") return "done"; // closed without one → treat as passed
    if (rank >= RANK[dueFrom]) return "current"; // it's due now
    return "upcoming";
  };

  const stateFor = (key: string): StepState => {
    const r = RANK[key];
    return rank < r ? "upcoming" : rank === r ? "current" : "done";
  };

  const steps: LifecycleStep[] = [
    { key: "draft", label: "Draft", state: stateFor("draft"), tab: "documents" },
    { key: "active", label: "Active", state: stateFor("active"), tab: "billing" },
    { key: "checkin", label: "Check-in", state: inspectionState(checkIn, "active"), tab: "inspections" },
    { key: "ending", label: "Ending", state: stateFor("ending"), tab: "documents" },
    { key: "checkout", label: "Check-out", state: inspectionState(checkOut, "ending"), tab: "inspections" },
    { key: "closed", label: "Closed", state: stateFor("closed"), tab: "deposit" },
  ];

  return { steps, hint: requirementHint(status, requirements) ?? HINT[status] };
}
